import React, { useState } from 'react';
import { ThemeToggle } from './theme';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function BulkStatusPortal() {
    const [emailsInput, setEmailsInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!emailsInput.trim()) return;

        // Extract emails
        const emails = emailsInput.split(/[\n, ]+/).filter(e => e.trim().includes('@'));

        if (emails.length === 0) {
            setError('Please enter at least one valid email address.');
            return;
        }

        if (emails.length > 100) {
            setError('Maximum 100 emails allowed per request.');
            return;
        }

        setLoading(true);
        setError(null);
        setResults(null);

        try {
            const res = await fetch(`${API_URL}/bulk-user-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emails })
            });
            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Failed to fetch statuses');
            } else {
                setResults(data.results);
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }
        setLoading(false);
    };

    const getStatusBadge = (status) => {
        let bg = '#6b7280';
        let text = 'Not Found';
        if (status === 'active') { bg = '#10b981'; text = 'Active'; }
        if (status === 'expiring-soon') { bg = '#f59e0b'; text = 'Expiring Soon'; }
        if (status === 'expired') { bg = '#ef4444'; text = 'Expired'; }
        if (status === 'migrating') { bg = '#0b7da6'; text = 'Migrating'; }

        return (
            <span style={{
                padding: '4px 12px',
                borderRadius: '50px',
                background: bg,
                color: 'white',
                fontWeight: '600',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                {text}
            </span>
        );
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#17242f',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px 20px',
            fontFamily: "'Segoe UI', system-ui, sans-serif"
        }}>
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px'
            }}>
                <ThemeToggle compact />
            </div>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px', marginTop: '20px' }}>
                <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <span style={{ color: '#8ce4ff' }}><Icon name="search" size={38} /></span>
                </div>
                <h1 style={{ color: 'white', fontSize: '32px', fontWeight: '700', marginBottom: '12px' }}>
                    Bulk Status Checker
                </h1>
                <p style={{ color: '#94a3b8', fontSize: '16px', maxWidth: '500px' }}>
                    Check up to 100 Adobe subscriptions at once. Paste emails separated by commas, spaces, or newlines.
                </p>
            </div>

            {/* Main Card */}
            <div style={{
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '24px',
                padding: '32px',
                width: '100%',
                maxWidth: '900px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
            }}>
                <form onSubmit={handleSearch}>
                    <textarea
                        value={emailsInput}
                        onChange={(e) => setEmailsInput(e.target.value)}
                        placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                        style={{
                            width: '100%',
                            height: '150px',
                            padding: '20px',
                            fontSize: '15px',
                            background: 'rgba(0,0,0,0.2)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '16px',
                            color: 'white',
                            outline: 'none',
                            fontFamily: 'monospace',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                    />

                    <button
                        type="submit"
                        disabled={loading || !emailsInput.trim()}
                        style={{
                            width: '100%',
                            padding: '16px',
                            marginTop: '20px',
                            background: loading ? '#475569' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                        }}
                    >
                        {loading ? 'Checking Statuses...' : 'Check Status'}
                    </button>
                </form>

                {error && (
                    <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', color: '#fca5a5' }}>
                        {error}
                    </div>
                )}

                {/* Results Table */}
                {results && results.length > 0 && (
                    <div style={{ marginTop: '32px', overflowX: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', color: 'white' }}>
                            <h3 style={{ margin: 0 }}>Results ({results.length})</h3>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: '13px', textTransform: 'uppercase' }}>
                                    <th style={{ padding: '12px', fontWeight: '600' }}>Email</th>
                                    <th style={{ padding: '12px', fontWeight: '600' }}>Status</th>
                                    <th style={{ padding: '12px', fontWeight: '600' }}>Organization</th>
                                    <th style={{ padding: '12px', fontWeight: '600' }}>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#e2e8f0' }}>
                                        <td style={{ padding: '14px 12px', fontWeight: '500' }}>{r.email}</td>
                                        <td style={{ padding: '14px 12px' }}>{getStatusBadge(r.status)}</td>
                                        <td style={{ padding: '14px 12px', fontSize: '14px' }}>
                                            {r.organizationName || '-'}
                                        </td>
                                        <td style={{ padding: '14px 12px', fontSize: '13px', color: '#94a3b8' }}>
                                            {r.status === 'migrating' && (
                                                <span>Queue: #{r.migrationQueuePosition}</span>
                                            )}
                                            {r.daysRemaining !== null && (
                                                <span>{r.daysRemaining} days left</span>
                                            )}
                                            {r.status === 'not_found' && '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={{ marginTop: '40px', color: '#64748b', fontSize: '14px' }}>
                For support, contact your reseller or administrator
            </div>
        </div>
    );
}

export default BulkStatusPortal;
